import { useMemo } from 'react';
import { mdiArrowDown, mdiArrowUp } from '@mdi/js';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
} from '@tanstack/react-table';
import { Table } from '@trussworks/react-uswds';
import Icon from '@components/icon/Icon';
import AdditionalInfoActions from './AdditionalInfoActions';
import Tooltip from '@components/tooltip/tooltip';
import Pagination from '@components/pagination/Pagination';
import { useAdditionalInfoStore } from '@src/stores';
import { AdditionalInfoFilterStatuses } from '@src/utils/enums';
import './additionalInfoTable.scss';

const isOverdue = (date) => {
  if (!date) return false;
  const due = new Date(date);
  const today = new Date();
  const dueUTC = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return dueUTC < todayUTC;
};

const getDueDateClass = (status, dueDate) => {
  const isOpen = status.toLowerCase() === AdditionalInfoFilterStatuses.Open.toLowerCase();
  return isOpen && isOverdue(dueDate) ? 'overdue-date' : '';
};

const AdditionalInfoTable = ({ additionalInfoRequests }) => {
  const {
    sortColumn,
    sortOrder,
    setSortColumn,
    setSortOrder,
    pageNumber,
    setPageNumber,
    itemsPerPage,
    setItemsPerPage,
    totalCount,
  } = useAdditionalInfoStore();

  const sorting = useMemo(
    () => (sortColumn ? [{ id: sortColumn, desc: sortOrder === 'desc' }] : []),
    [sortColumn, sortOrder]
  );

  const handleSortingChange = (updater) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      setSortColumn(newSorting[0].id);
      setSortOrder(newSorting[0].desc ? 'desc' : 'asc');
    } else {
      setSortColumn(null);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(
    () => [
      columnHelper.display({
        header: 'Actions',
        cell: AdditionalInfoActions,
        size: 50,
        enableSorting: false,
        meta: { centerText: true },
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        size: 50,
        cell: ({ cell }) => <span style={{ whiteSpace: 'nowrap' }}>{cell.getValue()}</span>,
        enableSorting: true,
        meta: { centerText: true },
      }),
      columnHelper.accessor('projectName', {
        header: 'Project Name',
        size: 300,
        cell: ({ cell }) => <span style={{ whiteSpace: 'nowrap' }}>{cell.getValue()}</span>,
        enableSorting: true,
      }),
      columnHelper.accessor('requestType', {
        header: 'Request Type',
        size: 50,
        cell: ({ cell }) => <span style={{ whiteSpace: 'nowrap' }}>{cell.getValue()}</span>,
        enableSorting: true,
      }),
      columnHelper.accessor('dueDate', {
        header: 'Due Date',
        size: 50,
        cell: ({ cell, row }) => {
          const value = cell.getValue();
          const { status } = row?.original;
          return <span className={getDueDateClass(status, value)}>{value}</span>;
        },
        enableSorting: true,
        meta: { centerText: true },
      }),
    ],
    [columnHelper]
  );
  const pageCount = Math.ceil(totalCount / itemsPerPage);
  const table = useReactTable({
    columns,
    data: additionalInfoRequests,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize: itemsPerPage,
      },
      sorting,
    },
    onSortingChange: handleSortingChange,
    manualPagination: true,
    pageCount,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handlePageSizeSelect = (pageSize) => {
    setItemsPerPage(pageSize);
    setPageNumber(1);
    table.setPageSize(pageSize);
  };

  const handleSortClick = (e, header) => {
    if (e.target.closest('.info-tooltip-icon')) {
      e.stopPropagation();
      return;
    }
    if (header.column.columnDef.enableSorting) {
      header.column.toggleSorting(header.column.getIsSorted() === 'asc');
    }
  };

  return (
    <div className='pn-table-container'>
      <Table bordered fullWidth>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  onClick={(e) => handleSortClick(e, header)}
                  colSpan={header.colSpan}
                  style={{ position: 'relative', userSelect: 'none' }}
                >
                  <div
                    style={{
                      cursor: header.column.columnDef.enableSorting ? 'pointer' : 'default',
                      position: 'relative',
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.columnDef.meta?.tooltip && (
                      <Tooltip
                        header={header.column.columnDef.header}
                        content={header.column.columnDef.meta?.tooltip}
                      />
                    )}
                    {
                      {
                        asc: <Icon path={mdiArrowUp} size='16px' />,
                        desc: <Icon path={mdiArrowDown} size='16px' />,
                      }[header.column.getIsSorted() || null]
                    }
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel()?.rows?.length === 0 && (
            <tr>
              <td colSpan={100}>NO RECORDS TO DISPLAY</td>
            </tr>
          )}
          {table.getRowModel()?.rows?.map((row) => (
            <tr key={row.id} className={row.getIsSelected() ? 'selected-row' : ''}>
              {row.getVisibleCells().map((cell) => (
                <td key={`${row.id}-${cell.id}`} style={{ width: cell?.column?.columnDef?.size + 'px' }}>
                  <div
                    className={`pn-cell display-flex ${cell?.column?.columnDef?.meta?.centerText ? 'text-center' : ''}`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
      {totalCount > 0 && (
        <Pagination
          currentPage={pageNumber}
          itemsPerPage={itemsPerPage}
          onPageChange={setPageNumber}
          onPageSizeChange={handlePageSizeSelect}
          pageCount={pageCount}
          rightContent
        />
      )}
    </div>
  );
};
export default AdditionalInfoTable;
